/**
 * Conversion between the stored description format and the HTML shown in the
 * contentEditable description editor.
 *
 * The stored format stays the markdown-ish one that util/richDescription renders on the
 * listing page and in the review preview: "**bold**", "- item" lines and "[label](url)".
 * The editor shows real bold text and real bullet points instead of those markers, so the
 * host never sees the raw syntax.
 */

const BULLET_LINE_REGEX = /^[ \t]*[-*•][ \t]+(.*)$/;
const BOLD_BLOCK_REGEX = /\*\*([\s\S]*?)\*\*/g;
const BOLD_TOKEN_REGEX = /\*\*([^*\n]+)\*\*/g;
const LINK_TOKEN_REGEX = /\[([^\]\n]*)\]\(([^)\s]*)\)/g;

// Heal link tokens whose label and URL got separated by whitespace, so the editor shows
// them as links too and writes the token back in one piece on the next save.
const joinSplitLinkTokens = text =>
  text.replace(
    /\]\s+\(\s*((?:https?:\/\/|\/\/|www\.)[^\s)]+|[^\s()]+\.[a-z]{2,}[^\s)]*)\s*\)/gi,
    (token, href) => `](${href})`
  );

const wrapBoldLine = text => {
  const trimmed = text.trim();
  if (!trimmed) {
    return text;
  }
  const leading = text.slice(0, text.indexOf(trimmed[0]));
  const trailing = text.slice(leading.length + trimmed.length);
  return `${leading}**${trimmed}**${trailing}`;
};

const normalizeMultilineBoldTokens = text =>
  text.replace(BOLD_BLOCK_REGEX, (token, boldText) => {
    if (!boldText.includes('\n')) {
      return token;
    }

    return boldText
      .split('\n')
      .map(line => {
        const bulletMatch = line.match(/^([ \t]*[-*•][ \t]+)(.*)$/);
        return bulletMatch
          ? `${bulletMatch[1]}${wrapBoldLine(bulletMatch[2])}`
          : wrapBoldLine(line);
      })
      .join('\n');
  });

export const normalizeDescriptionTokens = value =>
  typeof value === 'string'
    ? normalizeMultilineBoldTokens(joinSplitLinkTokens(value))
    : value;

const escapeHtml = text =>
  String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const inlineTokensToHtml = line =>
  escapeHtml(line)
    .replace(BOLD_TOKEN_REGEX, '<strong>$1</strong>')
    .replace(
      LINK_TOKEN_REGEX,
      (token, label, href) => `<a href="${href}">${label}</a>`
    );

/**
 * Stored description -> editor HTML.
 *
 * @param {String} value description in the stored token format
 * @returns {String} HTML for the contentEditable element
 */
export const descriptionToHtml = value => {
  if (typeof value !== 'string' || !value) {
    return '';
  }

  const blocks = [];
  const lines = normalizeDescriptionTokens(value).split('\n');
  lines.forEach(line => {
    const bulletMatch = line.match(BULLET_LINE_REGEX);
    const previousBlock = blocks[blocks.length - 1];
    if (bulletMatch) {
      if (previousBlock?.type === 'list') {
        previousBlock.items.push(bulletMatch[1]);
      } else {
        blocks.push({ type: 'list', items: [bulletMatch[1]] });
      }
    } else {
      blocks.push({ type: 'line', text: line });
    }
  });

  return blocks
    .map(block => {
      if (block.type === 'list') {
        const items = block.items
          .map(item => `<li>${inlineTokensToHtml(item) || '<br>'}</li>`)
          .join('');
        return `<ul>${items}</ul>`;
      }
      return `<div>${inlineTokensToHtml(block.text) || '<br>'}</div>`;
    })
    .join('');
};

const isElement = node => node?.nodeType === 1;
const isTextNode = node => node?.nodeType === 3;

// Bold markers must hug the text ("**bold** " not "**bold **"), otherwise the renderer
// keeps the stray space inside the <strong>.
const wrapBold = text => {
  const trimmed = text.trim();
  if (!trimmed) {
    return text;
  }
  const leading = text.slice(0, text.indexOf(trimmed[0]));
  const trailing = text.slice(leading.length + trimmed.length);
  return `${leading}**${trimmed}**${trailing}`;
};

const serializeInline = node => {
  if (isTextNode(node)) {
    return node.nodeValue.replace(/\u00a0/g, ' ');
  }
  if (!isElement(node)) {
    return '';
  }

  const tagName = node.nodeName;
  const inner = Array.from(node.childNodes).map(serializeInline).join('');

  if (tagName === 'BR') {
    return '';
  }
  if (tagName === 'STRONG' || tagName === 'B') {
    return wrapBold(inner);
  }
  if (tagName === 'A') {
    const href = node.getAttribute('href') || '';
    return inner ? `[${inner}](${href})` : '';
  }
  return inner;
};

const isEmptyBlock = node =>
  node.childNodes.length === 0 ||
  (node.childNodes.length === 1 && node.firstChild.nodeName === 'BR');

const serializeNode = (node, lines) => {
  if (isTextNode(node)) {
    lines[lines.length - 1] += node.nodeValue.replace(/\u00a0/g, ' ');
    return;
  }
  if (!isElement(node)) {
    return;
  }

  const tagName = node.nodeName;

  if (tagName === 'BR') {
    lines.push('');
    return;
  }

  if (tagName === 'UL' || tagName === 'OL') {
    if (lines[lines.length - 1] !== '') {
      lines.push('');
    }
    Array.from(node.children)
      .filter(child => child.nodeName === 'LI')
      .forEach(item => {
        lines[lines.length - 1] = `- ${serializeInline(item).trim()}`;
        lines.push('');
      });
    return;
  }

  if (['DIV', 'P', 'LI', 'BLOCKQUOTE'].includes(tagName)) {
    if (lines[lines.length - 1] !== '') {
      lines.push('');
    }
    if (!isEmptyBlock(node)) {
      Array.from(node.childNodes).forEach(child => serializeNode(child, lines));
    }
    lines.push('');
    return;
  }

  lines[lines.length - 1] += serializeInline(node);
};

/**
 * Editor HTML -> stored description.
 *
 * @param {HTMLElement} element the contentEditable element
 * @returns {String} description in the stored token format
 */
export const htmlToDescription = element => {
  if (!element) {
    return '';
  }

  const lines = [''];
  Array.from(element.childNodes).forEach(child => serializeNode(child, lines));

  // A block always ends with a fresh empty line; drop that bookkeeping line.
  if (lines.length > 1 && lines[lines.length - 1] === '') {
    lines.pop();
  }
  return lines.join('\n');
};
